/**
 * CartStockValidator
 *
 * Runs after the cart renders (full page or drawer). For every line item whose
 * quantity exceeds real available stock it:
 *  1. Calls /cart/change.js to correct the quantity silently.
 *  2. Renders an inline warning next to the item.
 *  3. Triggers a cart re-render via PUB_SUB_EVENTS.cartUpdate so totals / counts update.
 *
 * Only acts on variants tracked by Shopify (inventory_management = 'shopify')
 * with a hard deny policy (inventory_policy = 'deny').
 * Products set to 'continue' (backorder) or without tracking are intentionally ignored.
 */

class CartStockValidator {
  constructor() {
    this.isValidating = false;
    this._init();
  }

  _init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._validateAll());
    } else {
      this._validateAll();
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === 'cart-stock-validator') return;
        setTimeout(() => this._validateAll(), 200);
      });
    }
  }

  _getInputs() {
    return document.querySelectorAll(
      'cart-items .quantity__input, cart-drawer-items .quantity__input'
    );
  }

  _getOverage(input) {
    const management = input.dataset.inventoryManagement;
    const policy = input.dataset.inventoryPolicy;
    const available = parseInt(input.dataset.inventoryQuantity, 10);
    const current = parseInt(input.value, 10);

    if (management !== 'shopify' || policy !== 'deny') return null;
    if (isNaN(available) || isNaN(current) || available < 0) return null;
    if (current <= available) return null;

    return {
      line: parseInt(input.dataset.index, 10),
      available,
      current,
    };
  }

  async _validateAll() {
    if (this.isValidating) return;

    const inputs = Array.from(this._getInputs());
    const offenders = inputs
      .map((input) => {
        const overage = this._getOverage(input);
        return overage ? { input, ...overage } : null;
      })
      .filter(Boolean);

    if (offenders.length === 0) return;

    this.isValidating = true;
    try {
      for (const offender of offenders) {
        await this._adjustLine(offender);
      }
    } finally {
      this.isValidating = false;
    }
  }

  async _adjustLine({ input, line, available }) {
    const body = JSON.stringify({ line, quantity: available });

    try {
      const response = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        body,
      });
      const state = await response.json();

      const cartItems =
        document.querySelector('cart-items') ||
        document.querySelector('cart-drawer-items');

      if (cartItems && typeof cartItems.onCartUpdate === 'function') {
        await cartItems.onCartUpdate();
        setTimeout(() => this._showMessage(line, available), 250);
      } else {
        this._showMessage(line, available);
      }
    } catch (err) {
      console.error('[CartStockValidator] Failed to adjust line', line, err);
    }
  }

  _showMessage(line, available) {
    const errorEl =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (!errorEl) return;

    const textEl = errorEl.querySelector('.cart-item__error-text');
    if (!textEl) return;

    const tpl =
      (window.cartStrings && window.cartStrings.quantityAdjusted) ||
      "Only [quantity] available. We've updated your cart.";

    textEl.textContent = tpl.replace('[quantity]', available);
    errorEl.setAttribute('role', 'alert');
  }
}

new CartStockValidator();
